/* eslint-disable jsx-a11y/click-events-have-key-events */
import SharedComponents, { ChannelIcon } from "@audacia-hq/shared/components";
import { formatNumber } from "@audacia-hq/shared/utils";
import { faAngleRight, faCircleCheck, faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Transition } from "@headlessui/react";
import dayjs from "dayjs";
import { Field, FormikProvider, useFormik } from "formik";
import dynamic from "next/dynamic";
import * as React from "react";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { useLocale } from "../../../context/LocaleContext";
import { useMember } from "../../../context/MemberContext";
import { getLastEstimatedItemAndSlot, renderLatenessWarning, useQueue } from "../../../context/QueueContext";
import { QueueActionResult } from "../../../graphql/mutations";
import { Expert } from "../../../models/expert";
import { PhoneNumber } from "../../../models/member";
import { joinQueue } from "../../../services/api";
import Button from "../../Button";
import DirectCallPhones from "../../experts/DirectCallPhones";
import ExpertQueueSummary from "../../experts/ExpertQueueSummary";
import PhoneNumberValidationModal from "../../members/PhoneNumberValidationModal";
import useMissingMemberData from "../MissingMemberDataHook";

import CloseButtons from "./CloseButtons";

const PhoneInput = dynamic(() => import("../../PhoneInput"));

interface Props {
  expert: Expert;
  isDirectConsultation: boolean;
  onClose: () => void;
  onError: (err: string) => void;
}

const PhoneForm: React.FC<Props> = ({
  expert, isDirectConsultation, onClose, onError,
}) => {
  const { t } = useTranslation("consultations");
  const locale = useLocale();
  const { member, handleMemberGet } = useMember();
  const { memberQueues } = useQueue();
  const [loading, setLoading] = useState<boolean>(false);
  const [queued, setQueued] = React.useState<boolean>(!!memberQueues[expert?.expertUid]);
  const [confirmed, setConfirmed] = useState<QueueActionResult>();
  const [memberPhone, setMemberPhone] = useState<PhoneNumber>();
  const [showDirectNumbers, setShowDirectNumbers] = useState<boolean>(false);

  const [phoneInputMode, setPhoneInputMode] = useState<"ADD" | "SELECT">("SELECT");
  const [toggleEnabled, setToggleEnabled] = useState<boolean>();
  const [showAvailabilityInputs, setShowAvailabilityInputs] = useState<boolean>();
  const [isDirectMask, setIsDirectMask] = useState<boolean>();

  const [phoneToValidate, setPhoneToValidate] = useState<string>();
  const lastEstimatedItemAndSlot = getLastEstimatedItemAndSlot(expert, -2);

  const {
    initialValues, validationRules, inputs, onSubmit: onMemberDataSubmit,
  } = useMissingMemberData({});

  const handleClose = (error?: string) => {
    setPhoneToValidate(undefined);
    setLoading(false);
    if (error) {
      toast.error(t(`common:errors.${error}`), { autoClose: 5000 });
    }
  };

  const handleValidated = (phoneUid: string) => {
    setPhoneToValidate(undefined);
    setLoading(false);
    handleMemberGet();
    formik.setFieldValue("phoneUid", phoneUid);
  };

  const enqueuePhone = async (phoneUid: string, memberPseudo: string, dropAt?: string) => {
    const { data, error } = await joinQueue({
      variables: {
        queueUid: expert.queue.queueUid,
        type: "PHONE",
        expertUid: expert.expertUid,
        language: locale.toUpperCase(),
        phoneUid,
        memberPseudo,
        dropAt,
      },
    });
    if (error) {
      onError(error.messages[0]);
    } else if (data?.queueMutations) {
      setConfirmed(data.queueMutations.joinQueue);
      localStorage.removeItem("consultationModal");
      handleMemberGet();
    }
    setIsDirectMask(undefined);
    setLoading(false);
  };

  const computeDropAt = (value: string | undefined, visible: boolean) => {
    if (!value || !visible) return undefined;
    const [hour, minute] = value.split(":").map((v) => parseInt(v, 10));
    const now = dayjs().second(0).millisecond(0);
    if (hour < now.hour() || minute < now.minute()) {
      return now.add(1, "day").hour(hour).minute(minute).toISOString();
    }
    return now.hour(hour).minute(minute).toISOString();
  };

  const setMask = React.useCallback(() => {
    setIsDirectMask(isDirectConsultation);
  }, [isDirectConsultation]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      phoneNumber: "",
      phoneUid: member?.profile?.phoneUidLastUsed || member?.phoneNumbers[0]?.phoneUid,
      dropAt: `${dayjs().add(6, "hour").hour().toString()
        .padStart(2, "0")}:${dayjs().minute().toString().padStart(2, "0")}`,
      ...initialValues,
    },
    validationSchema: Yup.object().shape({
      phoneUid: phoneInputMode === "SELECT" ? Yup.string().required(t("common:errors.required")) : undefined,
      phoneNumber: phoneInputMode === "ADD" ? Yup.string().required(t("common:errors.required")) : undefined,
      dropAt: Yup.string(),
      ...validationRules,
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (form, helpers) => {
      if (!expert) return;

      try {
        setLoading(true);
        await onMemberDataSubmit(form, helpers);
        formik.setFieldValue("phoneNumber", "");
        formik.setFieldValue("phoneUid", form.phoneUid);
        setPhoneInputMode("SELECT");
        setMask();
        await enqueuePhone(form.phoneUid, member?.profile?.pseudo || form?.pseudo, computeDropAt(form?.dropAt, showAvailabilityInputs));
      } catch (err) {
        toast.error(t("common:errors.generalError"));
        setIsDirectMask(undefined);
        setLoading(false);
      }
    },
  });

  const renderQueued = () => (
    <>
      <div className="flex flex-col text-sm p-6 bg-slate-100 rounded-sm gap-3">
        <div className="text-lg font-semibold py-2 text-center">{t("queue.alreadyQueuedTitle")}</div>
        <div className="flex flex-col gap-4 text-center">

          <div>
            {t("queue.alreadyQueuedText", {
              pseudo: expert?.pseudo,
            })}
          </div>
        </div>
      </div>
      <CloseButtons onClose={onClose} />
    </>
  );

  const renderConfirmed = (memberPhone, servicePhone) => (
    <>
      <div className="col-span-6 md:col-span-4 md:col-start-2">
        <div>
          <div className="w-full text-center ">

            <div className="text-lg font-semibold ">{t("consultationForm.phone.confirmationTitle")}</div>
            <FontAwesomeIcon
              className="text-3xl z-10 text-green-500 mt-5"
              icon={faCircleCheck}
            />
            <div className="mt-5 mb-3">
              {t("consultationForm.phone.confirmationText", {
                pseudo: expert?.pseudo,
              })}
            </div>

            {(memberPhone || servicePhone) && (
              <div className="py-5 bg-slate-100 rounded-xs">
                {memberPhone && (
                  <div className="mb-2">
                    <Trans
                      i18nKey="queue.memberPhone"
                      ns="consultations"
                      values={{
                        memberPhone: formatNumber(memberPhone.phoneNumber, "INTERNATIONAL", memberPhone.country),
                      }}
                      components={[
                        <span className="font-semibold whitespace-nowrap">
                          memberPhoneText
                        </span>,
                      ]}
                    />
                  </div>
                )}

                {servicePhone && (
                  <div className="">
                    <Trans
                      i18nKey="queue.servicePhone"
                      ns="consultations"
                      values={{
                        servicePhone: formatNumber(servicePhone, "INTERNATIONAL"),
                      }}
                      components={[
                        <span className="font-semibold whitespace-nowrap">
                          servicePhoneText
                        </span>,
                      ]}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          {renderLatenessWarning(lastEstimatedItemAndSlot.item?.lateness, t)}
        </div>
        <CloseButtons onClose={onClose} />
      </div>
    </>
  );

  useEffect(() => {
    setQueued(!!memberQueues[expert?.expertUid]);
  }, [memberQueues, expert]);

  useEffect(() => {
    if (!member) return;
    setPhoneInputMode(member.phoneNumbers?.length ? "SELECT" : "ADD");
    setToggleEnabled(!!member.phoneNumbers?.length);
  }, [member]);

  useEffect(() => {
    if (!confirmed?.memberPhoneUid) {
      setMemberPhone(undefined);
      return;
    }
    setMemberPhone(member?.phoneNumbers?.find(({ phoneUid }) => phoneUid === confirmed.memberPhoneUid));
  }, [confirmed?.memberPhoneUid, member?.phoneNumbers]);

  useEffect(() => {
    if (formik.values.phoneUid) {
      setPhoneToValidate(member.phoneNumbers.filter((p) => !p.status).find((p) => p.phoneUid === formik.values.phoneUid)?.phoneNumber);
    }
  }, [formik.values.phoneUid]);

  return (
    <>
      {confirmed && renderConfirmed(memberPhone, confirmed?.servicePhone)}
      {!confirmed && queued && renderQueued()}
      {!confirmed && !queued && (
        <>
          <ExpertQueueSummary channel="PHONE" expert={expert} className="mb-12" />

          <div className="text-center font-bold my-6">
            {t("consultationForm.phone.weCall")}
            {" "}
            <SharedComponents.Tooltip
              groupClassName="inline"
              className="w-64 z-10"
              arrow
              position="bottom"
              message={(
                <div className="flex flex-col text-center gap-4 p-2">
                  <div>{t("consultationForm.howTo.phoneCallback.1")}</div>
                  <div className="font-semibold">{t("consultationForm.howTo.phoneCallback.2")}</div>
                </div>
              )}
            >
              <FontAwesomeIcon icon={faCircleQuestion} />
            </SharedComponents.Tooltip>
          </div>

          <FormikProvider value={formik}>
            {(() => {
              const inputFields = inputs(formik);

              return (
                <div className="flex flex-col items-center gap-4 my-3 w-full">
                  {inputFields.pseudo && (
                    <div className="w-full flex flex-col gap-1 justify-stretch">
                      {inputFields.pseudo()}
                    </div>
                  )}
                  {inputFields.birthday && (
                    <div className="w-full flex flex-col gap-1 ">
                      {inputFields.birthday()}
                    </div>
                  )}
                </div>
              );
            })()}

            {phoneInputMode === "SELECT" && (
              <div className="flex flex-col gap-1">
                <label htmlFor="phoneSelect" className="text-left block">
                  {t("consultationForm.phone.selectPhone")}
                </label>
                <select
                  id="phoneSelect"
                  onChange={(e) => formik.setFieldValue("phoneUid", e.target.value)}
                  value={formik.values.phoneUid}
                  className="input text-sm"
                >
                  {member.phoneNumbers.map((p) => (
                    <option value={p.phoneUid} key={p.phoneUid}>
                      {formatNumber(p.phoneNumber, "INTERNATIONAL", p.country)}
                    </option>
                  ))}
                </select>
                {toggleEnabled && (
                  <div className="text-center">
                    <button
                      className="cursor-pointer text-blue-700 text-sm mt-2"
                      onClick={() => setPhoneInputMode("ADD")}
                      type="button"
                      data-pw="addNewPhoneToggle"
                    >
                      {t("consultationForm.phone.useNewPhone")}
                    </button>
                  </div>
                )}
              </div>
            )}

            {phoneInputMode === "ADD" && (
              <div className="flex flex-col gap-1">
                <label htmlFor="phoneSelect">
                  {t("consultationForm.phone.addPhone")}
                </label>
                <PhoneInput
                  name="phoneNumber"
                  className="input"
                  selectedCountry={member?.country}
                  acceptedPhoneTypes={["FIXED_LINE", "MOBILE", "FIXED_LINE_OR_MOBILE"]}
                  dropdownClassName="h-28"
                  dataPw="phoneNumberInput"
                />
                {!!member?.phoneNumbers?.length && toggleEnabled && (
                  <button
                    className="w-full text-center cursor-pointer text-blue-700 text-sm mt-2"
                    onClick={() => setPhoneInputMode("SELECT")}
                    type="button"
                  >
                    {t("consultationForm.phone.useExistingPhone")}
                  </button>
                )}
              </div>
            )}

            {showAvailabilityInputs && (
              <Field name="dropAt">
                {({ field }) => (
                  <div className="my-6 flex flex-col gap-1">
                    <label htmlFor="phoneSelect">
                      {t("consultationForm.dropAt")}
                    </label>
                    <div className="flex flex-row flex-nowrap gap-4 items-baseline w-full">
                      <input
                        type="time"
                        className="input flex-grow"
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...field}
                      />
                      <div
                        className="cursor-pointer text-blue-700 grow-0"
                        onClick={() => setShowAvailabilityInputs(false)}
                      >
                        {t("common:cancel")}
                      </div>
                    </div>
                  </div>
                )}
              </Field>
            )}

            {!(isDirectMask || isDirectConsultation) && !showAvailabilityInputs && (
              <div
                className="text-center cursor-pointer text-blue-700 text-sm mt-6"
                onClick={() => setShowAvailabilityInputs(true)}
              >
                {t("consultationForm.setAvailabilityToggle")}
              </div>
            )}

            <div className="pt-6 text-center">
              {(phoneInputMode === "ADD") ? (
                <Button
                  loading={loading}
                  disabled={!formik.values.phoneNumber || !!formik.errors.phoneNumber}
                  render="primary"
                  onClick={() => setPhoneToValidate(formik.values.phoneNumber)}
                >
                  {t("consultationForm.phone.addPhoneButton")}
                </Button>
              ) : (
                <Button
                  size="large"
                  render="primary"
                  onClick={() => formik.submitForm()}
                  loading={loading}
                  data-pw="startPhoneConsultation"
                >
                  <span className="mr-3"><ChannelIcon channel="PHONE" className="text-white" /></span>
                  <span>{!(isDirectMask || isDirectConsultation) ? t("common:enqueue") : t("common:start")}</span>
                </Button>
              )}
            </div>

          </FormikProvider>
          {phoneToValidate && (
            <PhoneNumberValidationModal
              phoneNumber={phoneToValidate}
              onClose={handleClose}
              onValidated={handleValidated}
            />
          )}

          {(isDirectMask || isDirectConsultation) && (
            <>
              <div className="text-center font-bold mt-12 mb-6">
                <button type="button" className="font-bold" onClick={() => setShowDirectNumbers((prev) => !prev)}>
                  <span className="mr-2">
                    <FontAwesomeIcon
                      icon={faAngleRight}
                      className={`ml-1 transform transition duration-200 ease-in-out ${showDirectNumbers ? "rotate-90" : ""}`}
                    />
                  </span>
                  {t("consultationForm.phone.youCall")}
                </button>
              </div>

              <Transition
                show={showDirectNumbers}
                enter="transition ease-out duration-200"
                enterFrom="transform opacity-0 -translate-y-2"
                enterTo="transform opacity-200 translate-y-0"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-200 translate-y-0"
                leaveTo="transform opacity-0 -translate-y-2"
              >
                <DirectCallPhones expert={expert} />
              </Transition>
            </>
          )}
        </>

      )}
    </>
  );
};

export default PhoneForm;
